/**
 * useUserInfo Hook - Centralized user information fetching
 *
 * Provides consistent user info across all pages with proper caching.
 * Uses the same query key to ensure data is shared between components.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient, getStoredToken } from '@/lib/api';

/** Shape returned by useUserInfo — matches /v2/me merged with billing data. */
export interface UserInfoData {
  user: {
    email: string;
    name?: string;
    id: string;
    apiKey: string;
    githubToken?: string;
    subscription?: string;
    status?: string;
    stripeCustomerId?: string;
  };
  subscription?: {
    tier: string;
    tierName?: string;
    status?: string;
    subscriptionId?: string;
    trialEndsAt?: string;
    trialUsedAt?: string;
    periodEnd?: string;
    currentPeriodEnd?: string;
    seats?: number | null;
    upcomingInvoice?: { amountDue: number; dueDate?: string };
  };
  limits?: { scansRemaining: number; scansUsed: number; scansPerMonth: number };
  subscriptions?: {
    id: string;
    tier: string;
    tierName: string;
    status: string;
    periodEnd: string;
  }[];
}

/** Minimal user info returned when the API is unreachable or subscription is invalid. */
const FREE_TIER_FALLBACK: UserInfoData = {
  user: {
    email: '',
    name: undefined as string | undefined,
    id: '',
    apiKey: '',
    subscription: undefined as string | undefined,
    status: 'active' as string | undefined,
    stripeCustomerId: undefined as string | undefined,
  },
  subscription: {
    tier: 'free',
    tierName: 'Free',
    status: 'active',
    trialEndsAt: undefined as string | undefined,
    trialUsedAt: undefined as string | undefined,
    periodEnd: undefined as string | undefined,
    currentPeriodEnd: undefined as string | undefined,
    seats: null as number | null,
    upcomingInvoice: undefined as { amountDue: number; dueDate?: string } | undefined,
  },
  limits: { scansRemaining: 0, scansUsed: 0, scansPerMonth: 0 },
  subscriptions: [] as {
    id: string;
    tier: string;
    tierName: string;
    status: string;
    periodEnd: string;
  }[],
};

export const useUserInfo = () => {
  const { authenticated, token, setApiKey } = useAuth();

  return useQuery({
    queryKey: ['userInfo', 'me'],
    queryFn: async () => {
      const authToken = token || getStoredToken();
      if (!authToken) {
        return FREE_TIER_FALLBACK;
      }

      try {
        // Fetch /v2/me and /v2/billing/subscription in parallel — billing is
        // the canonical live-tier source; /v2/me can briefly lag after a sync.
        const [data, billingSubscription] = await Promise.all([
          apiClient.getMe(authToken),
          apiClient.getSubscription(authToken).catch((err) => {
            console.warn('[useUserInfo] Billing subscription unavailable, using /v2/me data:', err);
            return null;
          }),
        ]);

        if (billingSubscription) {
          data.subscription = {
            ...(data.subscription ?? { tier: 'free', tierName: 'Free', status: 'active' }),
            tier: billingSubscription.tier || data.subscription?.tier || 'free',
            tierName: billingSubscription.tierName || data.subscription?.tierName,
            status: billingSubscription.status || data.subscription?.status,
            periodEnd: billingSubscription.currentPeriodEnd || data.subscription?.periodEnd,
            currentPeriodEnd: billingSubscription.currentPeriodEnd || data.subscription?.currentPeriodEnd,
            trialEndsAt: billingSubscription.trialEnd || data.subscription?.trialEndsAt,
            seats: billingSubscription.seats ?? data.subscription?.seats,
            upcomingInvoice: billingSubscription.upcomingInvoice ?? data.subscription?.upcomingInvoice,
          };
        }

        // Cache the API key in context + localStorage when returned from the server.
        if (data?.user?.apiKey) {
          setApiKey(data.user.apiKey);
        }

        // Expose all active subscriptions for the multi-subscription selector
        if (billingSubscription?.subscriptions && billingSubscription.subscriptions.length > 0) {
          return { ...data, subscriptions: billingSubscription.subscriptions };
        }

        return data;
      } catch (err) {
        // Any non-auth failure (network, 402, 5xx) degrades gracefully to free
        // tier instead of breaking the dashboard. Auth failures (401) are
        // already handled globally by checkAuthResponse → handleAuthFailure.
        console.warn('[useUserInfo] Failed to load user info, using free-tier fallback:', err);
        return FREE_TIER_FALLBACK;
      }
    },
    enabled: authenticated,
    staleTime: 5 * 60 * 1000,
    retry: 0, // Don't retry — failures degrade to free-tier fallback above
  });
};
